import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { ProductId, PRODUCTS } from '../types/production';

export interface CostPerBagResult {
  unitId: string;
  unitName: string;
  totalApprovedExpenses: number;
  totalProductionBags: number;
  costPerBag: number;
  includedProducts: ProductId[];
  period: {
    startDate: string;
    endDate: string;
  };
  breakdown: {
    product: string;
    bags: number;
    contribution: number;
  }[];
}

export interface CostRollupOptions {
  unitId: string;
  startDate: string;
  endDate: string;
  includeAllSagoTypes?: boolean;
  approvedOnly?: boolean;
}

/**
 * Service for calculating cost per bag metrics
 * Computes ₹/bag = Total Approved Expenses ÷ Total Production Bags
 */
export class CostRollupService {
  /**
   * Calculate cost per bag for a unit over a date range
   */
  static async calculateCostPerBag(options: CostRollupOptions): Promise<CostPerBagResult> {
    const { unitId, startDate, endDate, includeAllSagoTypes = true, approvedOnly = true } = options;

    // Determine which products to include
    const includedProducts = this.getIncludedProducts(includeAllSagoTypes);

    // Fetch approved expenses for the period
    const totalExpenses = await this.getTotalApprovedExpenses(unitId, startDate, endDate, approvedOnly);

    // Fetch total production bags for included products
    const productionData = await this.getTotalProductionBags(unitId, startDate, endDate, includedProducts);

    // Calculate cost per bag
    const totalBags = productionData.totalBags;
    const costPerBag = totalBags > 0 ? totalExpenses / totalBags : 0;

    // Get unit name
    const unitName = await this.getUnitName(unitId);

    return {
      unitId,
      unitName,
      totalApprovedExpenses: totalExpenses,
      totalProductionBags: totalBags,
      costPerBag,
      includedProducts,
      period: { startDate, endDate },
      breakdown: productionData.breakdown
    };
  }

  /**
   * Get list of products to include based on toggle
   */
  private static getIncludedProducts(includeAllSagoTypes: boolean): ProductId[] {
    if (includeAllSagoTypes) {
      // Include all sago-related products
      return ['sago', 'polishBroken', 'vibratorBroken', 'dryerBroken', 'polishPowder'];
    } else {
      // Include only main Sago product
      return ['sago'];
    }
  }

  /**
   * Get total approved expenses for a unit and period
   */
  private static async getTotalApprovedExpenses(
    unitId: string,
    startDate: string,
    endDate: string,
    approvedOnly: boolean
  ): Promise<number> {
    const expensesRef = collection(db, 'expenses');

    let q = query(
      expensesRef,
      where('unitId', '==', unitId),
      where('date', '>=', startDate),
      where('date', '<=', endDate)
    );

    if (approvedOnly) {
      q = query(q, where('approvalStatus', '==', 'approved'));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.reduce((total, doc) => {
      const data = doc.data();
      return total + (data.amount || 0);
    }, 0);
  }

  /**
   * Get total production bags for included products
   */
  private static async getTotalProductionBags(
    unitId: string,
    startDate: string,
    endDate: string,
    includedProducts: ProductId[]
  ): Promise<{ totalBags: number; breakdown: { product: string; bags: number; contribution: number }[] }> {
    const productionRef = collection(db, 'productionDaily');

    const q = query(
      productionRef,
      where('unitId', '==', unitId),
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      orderBy('date', 'asc')
    );

    const snapshot = await getDocs(q);

    const productTotals: Record<string, number> = {};
    let totalBags = 0;

    snapshot.docs.forEach(doc => {
      const data = doc.data();
      const productId = data.productId as ProductId;

      if (includedProducts.includes(productId)) {
        const bags = data.producedBags || 0;
        productTotals[productId] = (productTotals[productId] || 0) + bags;
        totalBags += bags;
      }
    });

    // Create breakdown
    const breakdown = includedProducts.map(productId => {
      const bags = productTotals[productId] || 0;
      const contribution = totalBags > 0 ? (bags / totalBags) * 100 : 0;
      const productName = PRODUCTS.find(p => p.id === productId)?.name || productId;

      return {
        product: productName,
        bags,
        contribution: Math.round(contribution * 100) / 100 // Round to 2 decimal places
      };
    });

    return { totalBags, breakdown };
  }

  /**
   * Get unit name from unitId
   */
  private static async getUnitName(unitId: string): Promise<string> {
    const unitsRef = collection(db, 'units');
    const q = query(unitsRef, where('id', '==', unitId));
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      const unitData = snapshot.docs[0].data();
      return unitData.name || unitId;
    }

    return unitId;
  }

  /**
   * Calculate cost per bag for multiple units
   */
  static async calculateMultipleUnits(
    unitIds: string[],
    startDate: string,
    endDate: string,
    includeAllSagoTypes: boolean = true,
    approvedOnly: boolean = true
  ): Promise<CostPerBagResult[]> {
    const promises = unitIds.map(unitId =>
      this.calculateCostPerBag({
        unitId,
        startDate,
        endDate,
        includeAllSagoTypes,
        approvedOnly
      })
    );

    return Promise.all(promises);
  }

  /**
   * Get cost trends over time
   */
  static async getCostTrends(
    unitId: string,
    periods: { startDate: string; endDate: string; label: string }[],
    includeAllSagoTypes: boolean = true
  ): Promise<{ period: string; costPerBag: number; totalBags: number; totalExpenses: number }[]> {
    const promises = periods.map(async (period) => {
      const result = await this.calculateCostPerBag({
        unitId,
        startDate: period.startDate,
        endDate: period.endDate,
        includeAllSagoTypes,
        approvedOnly: true
      });

      return {
        period: period.label,
        costPerBag: result.costPerBag,
        totalBags: result.totalProductionBags,
        totalExpenses: result.totalApprovedExpenses
      };
    });

    return Promise.all(promises);
  }
}
